/**
 * Security & Data Protection — /security (iter98 · Item XX)
 *
 * Content is REAL — accurate to what the platform actually does. Every claim
 * maps to a real implementation:
 *   - bcrypt password hashing: server.py::register + login
 *   - HTTPS in transit: platform ingress
 *   - Field-level encryption at rest: iter98 crypto.py (Fernet, enc:v1: envelope)
 *   - WhatsApp mutual-connection gating: iter89 _public_profile_for_case
 *   - Audited admin whatsapp reveal: iter97 admin/users/{id}?reveal_whatsapp=1
 *   - Report-triggered admin conversation review: iter97 admin/reports/{id}/conversation
 *   - Aggregate-only telemetry: iter83 Wave 2 admin_analytics
 *   - Soft-delete + 14-day grace: iter85 account_lifecycle.py
 *
 * Copy signed off by founder — 2 August 2026.
 */
import { Link } from "react-router-dom";

export default function Security() {
  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="security-page">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Ministry of Exchange</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-ink">Security &amp; Data Protection</h1>
        <p className="mt-3 text-sm text-muted" data-testid="security-last-updated">
          Last updated: 2 August 2026
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-ink/85 [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-ink [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:ml-5 [&_ul]:list-disc [&_li]:mb-1">

          <section>
            <p>
              Ministry of Exchange is operated by Nikshala AI. Here&rsquo;s exactly how we protect student data
              &mdash; no vague claims, just what&rsquo;s actually built.
            </p>
          </section>

          <section>
            <h2>Passwords</h2>
            <p>
              Every password is hashed with bcrypt before storage. Nobody, including our own team, can
              ever see a user&rsquo;s actual password, even in the event of a data breach.
            </p>
          </section>

          <section>
            <h2>Data in transit</h2>
            <p>
              All data moving between your device and our servers is encrypted over HTTPS &mdash; standard,
              industry-wide protection against interception.
            </p>
          </section>

          <section>
            <h2>Sensitive data at rest</h2>
            <p>
              Private messages (direct messages, cohort group messages, trip/plan comments) and WhatsApp
              numbers are encrypted in our database itself, not just protected by access controls. This
              means even someone with direct access to a database backup or export cannot read this data
              without going through our application&rsquo;s own controlled decryption process.
            </p>
            <p>
              To be precise about what this does and doesn&rsquo;t mean: this is encryption at rest with
              controlled server-side decryption for legitimate access paths (you reading your own
              messages, a mutual connection revealing a WhatsApp number, or an admin reviewing a
              specifically reported conversation). It is not end-to-end encryption, where even we could
              never decrypt the data under any circumstance. We believe in being exact about this
              distinction rather than overstating it.
            </p>
          </section>

          <section>
            <h2>Who can see what</h2>
            <ul>
              <li><b>WhatsApp numbers</b> stay completely hidden from other students until both sides have mutually accepted a connection. Even our own team can only reveal a number through a specific, logged action &mdash; never standing access.</li>
              <li><b>Private messages</b> are not accessed by our team by default. Access only happens if a student specifically reports a conversation for a safety concern, and every single review is logged: who accessed it, when, and why.</li>
              <li><b>Usage analytics</b> are aggregate and funnel-level only. We do not maintain a browsable log of any individual person&rsquo;s click-by-click activity.</li>
            </ul>
          </section>

          <section>
            <h2>Account deletion</h2>
            <p>
              You can delete your account at any time. Deletion is soft with a 14-day reversible grace
              period (you can undo it via a link sent to your email), then permanently purged &mdash;
              consistent with GDPR&rsquo;s right to erasure.
            </p>
          </section>

          <section>
            <h2>Compliance</h2>
            <p>
              We operate under both the EU&rsquo;s GDPR (since our students are physically in EU host
              countries) and India&rsquo;s DPDP Act 2023 (as an Indian entity). We build to satisfy the
              stricter of the two, not the minimum of either.
            </p>
          </section>

          <section>
            <h2>Questions</h2>
            <p>
              If your institution&rsquo;s IT or legal team wants to review our practices in more detail,
              contact us at{" "}
              <a href="mailto:nd@nikshala.com" className="text-elec font-semibold underline">
                nd@nikshala.com
              </a>{" "}
              or{" "}
              <a href="tel:+918263907201" className="text-elec font-semibold underline">
                +91 82639 07201
              </a>.
              We&rsquo;re happy to walk through any of this directly.
            </p>
            <p>
              See also our{" "}
              <Link to="/privacy" className="text-elec font-semibold underline" data-testid="security-privacy-link">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/terms" className="text-elec font-semibold underline" data-testid="security-terms-link">
                Terms &amp; Conditions
              </Link>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
